import * as React from 'react';
// eslint-disable-next-line import/namespace
import { GestureResponderEvent } from 'react-native';
import { connect } from 'react-redux';

import { Button, Text, ValidatedTextInput } from '@opendoor/bricks-next';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { Box, Flex, Image } from '@opendoor/bricks/core';
import { createSelector } from '@reduxjs/toolkit';

import { referToMarkSpain, referToParn, referToRedfin } from 'src/api';
import {
  Column,
  ColumnContainer,
  TwoColumnPageContent,
} from 'src/components/layouts/TwoColumnPageLayout';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import imgixUrl from 'src/helpers/imgixUrl';
import { getPartnerByIdentifier, MARK_SPAIN, PARN, REDFIN } from 'src/helpers/partners';
import layoutWrapper from 'src/hocs/layoutWrapper';
import {
  address,
  denialPartnerIdentifier,
  lastCompletedSlug,
  leadToken,
} from 'src/store/sellerFlow/selectors';
import { SellerInputAddress } from 'src/store/sellerInput';

interface IProps {
  // Connected Props:
  address: SellerInputAddress;
  lastCompletedSlug: string;
  leadToken: string;
  denialPartnerIdentifier: string;
}

interface IState {
  name?: string;
  email?: string;
  hasPhoneError?: boolean;
  hasEmailError?: boolean;
  phone?: string;
  submitting: boolean;
  submitError: boolean;
  submitSuccess: boolean;
}

class NovoFlowCompletePartner extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      name: '',
      email: '',
      hasPhoneError: undefined,
      hasEmailError: undefined,
      phone: '',
      submitting: false,
      submitError: false,
      submitSuccess: false,
    };
  }

  private handleNameChange = (name: string) => {
    this.setState({ name });
  };

  private handleEmailChange = (email: string) => {
    this.setState({ email });
  };

  private handlePhoneChange = (phone: string) => {
    this.setState({ phone });
  };

  private handleSubmit = (e: GestureResponderEvent) => {
    e.preventDefault();
    if (this.state.name && this.state.email && this.state.phone) {
      this.submitPartnerReferral(this.state.name, this.state.email, this.state.phone);
    }
  };

  componentDidUpdate(_prevProps: Readonly<IProps>, prevState: Readonly<IState>): void {
    if (this.state.submitError && !prevState.submitError) {
      showToast(
        'error',
        'Oops, something went wrong! Please try again.',
        'reception-fe-partner-submit-interest-error-toast',
        {
          toastOptions: {
            autoClose: 7000,
          },
        },
      );
    }
  }

  public render() {
    const { address, denialPartnerIdentifier } = this.props;
    const { name, email, hasEmailError, hasPhoneError, phone, submitting, submitSuccess } =
      this.state;

    const disabled = !(name && email && phone && !hasEmailError && !hasPhoneError) || submitting;
    if (submitSuccess) {
      showToast(
        'info',
        'We have received your information. You’ll get a call from a local agent soon.',
        'reception-fe-partner-submit-interest-success-toast',
        {
          toastOptions: {
            autoClose: 7000,
          },
          actions: [
            {
              actionText: 'Back to homepage',
              onClick: () => {
                window.location.href = 'https://www.opendoor.com';
              },
              'aria-label': '',
            },
          ],
        },
      );
    }
    const partner = getPartnerByIdentifier(denialPartnerIdentifier);
    return (
      <TwoColumnPageContent>
        <ColumnContainer>
          <Column>
            <Flex
              flexDirection="column"
              width={['100%', '100%', '100%']}
              alignItems="flex-start"
              justifyContent="center"
              gap={[10, 20, 0]}
            >
              <Text
                color="$contentTertiary"
                typography="$labelMedium"
                tag="p"
                fontWeight="600"
                mb={20}
              >
                {address.street}
              </Text>
              <Text
                tag="p"
                mb={15}
                typography="$headerMedium"
                $largerThanSM={{
                  typography: '$headerSmall',
                  mb: 30,
                }}
                className="complete-partner-header"
              >
                We can't make an offer today
              </Text>
              <Text
                tag="p"
                typography="$labelMedium"
                fontWeight="600"
                mb={15}
                $largerThanSM={{
                  mb: 30,
                }}
                width={partner?.identifier === 'parn' ? '70%' : '100%'}
              >
                We recommend listing with
                {partner?.identifier !== 'parn' ? (
                  <Image
                    src={imgixUrl(partner?.logo || '')}
                    alt="Partner Logo"
                    width={
                      partner?.identifier === 'redfin'
                        ? '100px'
                        : partner?.identifier === 'mark_spain'
                        ? '70px'
                        : 0
                    }
                    height="auto"
                    ml={3}
                    my={
                      partner?.identifier === 'redfin'
                        ? -2
                        : partner?.identifier === 'mark_spain'
                        ? -5
                        : 0
                    }
                  />
                ) : (
                  <> an Expert Local Agent </>
                )}
              </Text>
              <Text
                width="90%"
                mb={8}
                style={{ overflowWrap: 'break-word' }}
                typography="$bodyMedium"
                tag="p"
              >
                Our partner agents are vetted to ensure they share the same customer service values
                as Opendoor.
                <br />
                <br />
                Enter your info and a local agent will reach out to answer any of your home selling
                questions.
              </Text>
            </Flex>
          </Column>
          <Column>
            <Flex
              flexDirection="column"
              width={['100%', '100%', '100%']}
              alignItems="strech"
              justifyContent="center"
              gap={[6, 6, 8]}
              mt={20}
            >
              <ValidatedTextInput
                analyticsName="reception-fe-flow-complete-partner-name"
                size="medium"
                title="Name"
                placeholder="Full name"
                onChangeText={this.handleNameChange}
                format="none"
                textContentType="name"
                required
                autoCorrect={false}
              />
              <ValidatedTextInput
                allowSuggestions
                analyticsName="reception-fe-flow-complete-partner-email"
                size="medium"
                title="Email"
                placeholder="example@email.com"
                onChangeText={this.handleEmailChange}
                format="emailAddress"
                required
                autoCorrect={false}
              />
              <ValidatedTextInput
                allowSuggestions
                analyticsName="reception-fe-flow-complete-partner-phone"
                size="medium"
                title="Phone number"
                placeholder="000-000-0000"
                onChangeText={this.handlePhoneChange}
                format="telephoneNumber"
                required
              />
              <Box
                mx={[-7, -7, 0]}
                position={['fixed', 'fixed', 'relative']}
                bottom={[0, 0, 'auto']}
                display={['flex', 'flex', 'content']}
                justifyContent={['center', 'center', 'normal']}
                alignItems={['center', 'center', 'normal']}
                padding={[4, 4, 0]}
                backgroundColor={['white0', 'white0', 'transparent']}
                shadow={['z2', 'z2', 'none']}
                width={['100%', '100%', '100%']}
              >
                <Button
                  label="Submit"
                  onPress={this.handleSubmit}
                  disabled={disabled}
                  loading={submitting}
                  size="medium"
                  variant="primary"
                  analyticsName="reception-fe-flow-complete-partner-submit-button"
                  width="90%"
                  $largerThanSM={{
                    width: '100%',
                  }}
                />
              </Box>
              <Text typography="$bodyMedium" tag="p">
                There's no obligation to sell
              </Text>
            </Flex>
          </Column>
        </ColumnContainer>
      </TwoColumnPageContent>
    );
  }

  private async submitPartnerReferral(name: string, email: string, phone: string) {
    this.setState({ submitting: true });
    const sellerInputId = this.props.leadToken;

    try {
      switch (this.props.denialPartnerIdentifier) {
        case MARK_SPAIN.identifier:
          await referToMarkSpain(sellerInputId, name, email, phone);
          break;
        case REDFIN.identifier:
          await referToRedfin(sellerInputId, name, email, phone);
          break;
        case PARN.identifier:
          await referToParn(sellerInputId, name, email, phone);
          break;
      }
      this.setState({ submitting: false, submitSuccess: true });
    } catch (e) {
      this.setState({ submitError: true, submitting: false, submitSuccess: false });
    }
  }
}

const mapStateToProps = createSelector(
  [address, lastCompletedSlug, leadToken, denialPartnerIdentifier],
  (
    address: SellerInputAddress,
    lastCompletedSlug: string,
    leadToken: string,
    denialPartnerIdentifier: string,
  ) => ({
    address,
    lastCompletedSlug,
    leadToken,
    denialPartnerIdentifier,
  }),
);

const NovoFlowCompletePartnerWithLayout = layoutWrapper(
  connect(mapStateToProps)(NovoFlowCompletePartner),
  { headerType: 'HIDE_NAV', footerType: 'MINIMAL', logoType: 'BLACK' },
);
const NovoFlowCompletePartnerWithLoader = () => (
  <ReceptionLoader>
    <Box pb={[70, 70, 0]}>
      <NovoFlowCompletePartnerWithLayout />
    </Box>
  </ReceptionLoader>
);
export default NovoFlowCompletePartnerWithLoader;
